import {useDeleteTask} from '@github-ui/agent-sessions/hooks/use-delete-task'
import {DeleteConversationDialog} from '@github-ui/copilot-shared-ui/components/DeleteConversationDialog'
import {useCallback, useEffect, useRef, type RefObject} from 'react'

import type {ConversationDeletion} from '../runtime/use-conversation-deleted-navigation'

export function DeleteConversationDialogContainer({
  open,
  taskId,
  conversationDeletion,
  onClose,
  returnFocusRef,
}: {
  open: boolean
  taskId: string
  conversationDeletion: ConversationDeletion
  onClose: () => void
  returnFocusRef: RefObject<HTMLElement | null>
}) {
  const {mutate, isPending, error, reset} = useDeleteTask({taskId, apiUrl: conversationDeletion.apiUrl})
  const deleteError = error ? 'Failed to delete chat. Please try again.' : null
  const deleteInFlightRef = useRef(false)

  const handleConfirm = useCallback(() => {
    if (deleteInFlightRef.current) return
    deleteInFlightRef.current = true
    mutate(undefined, {
      onSuccess: () => {
        onClose()
        conversationDeletion.onDeleted(taskId)
      },
      onSettled: () => {
        deleteInFlightRef.current = false
      },
    })
  }, [mutate, taskId, conversationDeletion, onClose])

  useEffect(() => {
    if (!open && !isPending) reset()
  }, [open, isPending, reset])

  return open ? (
    <DeleteConversationDialog
      title="Delete chat"
      message="Are you sure you want to delete this chat? This action cannot be undone."
      busyLabel="Deleting chat"
      isPending={isPending}
      error={deleteError}
      onConfirm={handleConfirm}
      onClose={onClose}
      returnFocusRef={returnFocusRef}
    />
  ) : null
}
